import React, { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  buscarVisaoGeral,
  type FiltrosDashboard,
  type RespostaVisaoGeral,
} from '../../../../api/contac/contacDashboardApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../../components/como-funciona/ComoFunciona';
import { notify } from '../../../../shared/feedback/notify';
import {
  corDaSerie,
  formatarMoeda,
  formatarMoedaCompacta,
  formatarNumero,
  formatarPercentual,
  TINTA_GRAFICO,
} from '../../../../shared/ui/chart-palette';
import {
  AreaEstado,
  CardGrafico,
  EIXO_LINHA,
  EIXO_TICK,
  ESTILO_TOOLTIP,
  Kpi,
  Legenda,
} from '../../../../shared/ui/GraficoBase';
import '../../../../components/como-funciona/help-circle-btn.css';
import '../../../../shared/feedback/erro-de-tela.css';
import '../../../../shared/feedback/aviso-de-tela.css';

const COR_EMITIDO = corDaSerie(0);
const COR_ARRECADADO = corDaSerie(1);

export const helpSectionsVisaoGeral: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta aba mostra',
    conteudo: (
      <>
        <p>
          O <strong>dinheiro da carteira</strong> no período escolhido: quanto foi cobrado, quanto
          entrou de fato, e como isso se distribui ao longo dos meses.
        </p>
        <p>
          Tudo vem da base financeira já sincronizada — a tela <strong>não</strong> consulta o
          sistema do SGA na hora. Por isso ela abre rápido, e por isso o número reflete a última
          sincronização, não o instante em que você está olhando.
        </p>
      </>
    ),
  },
  {
    titulo: 'Emitido, arrecadado e adimplência',
    conteudo: (
      <>
        <p>
          <strong>Emitido</strong> é a soma dos boletos daquela competência, tenham sido pagos ou
          não. <strong>Arrecadado</strong> soma apenas os boletos que <strong>têm data de
          pagamento</strong> registrada — boleto em aberto não entra, mesmo que já tenha um valor
          previsto.
        </p>
        <p>
          A <strong>adimplência</strong> é a proporção de boletos pagos sobre boletos emitidos, em
          quantidade (não em valor). Um mês com poucos boletos grandes em atraso pode ter
          adimplência alta e arrecadação baixa — os dois números respondem perguntas diferentes.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que o mês atual não aparece',
    conteudo: (
      <p>
        A base financeira fica disponível com <strong>dois meses de defasagem</strong>: em julho, o
        mês mais recente que se pode consultar é maio. O período que a tela está usando aparece
        escrito ao lado do botão de filtros. Não é falha de sincronização — é o prazo de fechamento
        dos dados.
      </p>
    ),
  },
  {
    titulo: 'Filtros de regional e categoria não valem aqui',
    conteudo: (
      <>
        <p>
          Se você aplicar <strong>Regional</strong> ou <strong>Categoria</strong>, esta aba avisa
          que eles foram ignorados — e o aviso é literal: os números continuam sendo os da empresa
          inteira (respeitada a base, essa sim aplicada).
        </p>
        <p>
          O motivo é a natureza do dado: o valor é cobrado por <strong>boleto</strong>, e um boleto
          pode cobrir vários veículos, de regionais e categorias diferentes. Dividir o valor entre
          eles exigiria uma regra de rateio que o sistema não tem — e um número rateado por
          adivinhação pareceria certo sem ser. Para recortar por regional ou categoria, use a aba{' '}
          <strong>Frota &amp; Associados</strong>, onde o dado é por veículo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quando a consulta falha',
    conteudo: (
      <p>
        Se os dados não puderem ser carregados, a aba mostra uma faixa vermelha e{' '}
        <strong>não</strong> desenha gráfico zerado. Gráfico vazio só significa "não houve
        movimento" quando <strong>não há</strong> aviso de erro na tela — com o aviso, significa
        que o sistema não conseguiu verificar.
      </p>
    ),
  },
];

interface Props {
  filtros: FiltrosDashboard;
  /** Alimenta os selects do modal de filtros com o que existe de fato nos dados. */
  onOpcoes?: (opcoes: { bases?: string[]; categorias?: string[] }) => void;
  /** Informa à página o período que o backend efetivamente usou (importa quando não há filtro). */
  onJanela?: (janela: { dataInicio: string; dataFim: string }) => void;
}

const VisaoGeralTab: React.FC<Props> = ({ filtros, onOpcoes, onJanela }) => {
  const [dados, setDados] = useState<RespostaVisaoGeral | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const carregar = useCallback(async (f: FiltrosDashboard) => {
    setCarregando(true);
    try {
      const resposta = await buscarVisaoGeral(f);
      setDados(resposta);
      setErro(null);
      onOpcoes?.({ bases: resposta.porBase.map((b) => b.base) });
      onJanela?.({ dataInicio: resposta.janela.dataInicio, dataFim: resposta.janela.dataFim });
    } catch (e) {
      // Estado seguro E sinal visível: os números somem (gráfico com o resultado do período
      // anterior seria lido como o resultado do período pedido) e `dados` fica null, então a aba
      // NÃO afirma que o período não teve movimento.
      setDados(null);
      setErro(
        'Não foi possível carregar os dados financeiros. Enquanto este aviso estiver na tela, ' +
          'não há número para ler aqui — recarregue a página e, se persistir, acione a TI.',
      );
      // TEXTO FIXO, não errorFrom (regra da at-65): o que importa é a CONSEQUÊNCIA (o que está na
      // tela não é resposta), não o motivo HTTP — num 403 o extrator devolveria "Você não tem
      // permissão para executar esta ação", que substitui a única informação útil aqui.
      notify.error(
        'Não foi possível carregar os dados financeiros do dashboard. Não interprete a tela como ' +
          'período sem movimento enquanto este aviso aparecer.',
        { toastId: 'dashboard-associacao-visao-geral-falhou' },
      );
    } finally {
      setCarregando(false);
    }
    // `onOpcoes` e `onJanela` são estáveis na página (useCallback com atualização funcional);
    // listá-los aqui é correto e não relança a carga.
  }, [onOpcoes, onJanela]);

  useEffect(() => {
    void carregar(filtros);
  }, [carregar, filtros]);

  const respondeu = dados !== null;
  /** Primeira carga: nada na tela ainda. Numa reconsulta, os dados anteriores seguem visíveis. */
  const primeiraCarga = carregando && !respondeu;
  const vazio = respondeu && dados.kpis.boletosEmitidos === 0;

  /** Texto do estado vazio: afirma ausência SÓ quando a consulta respondeu. */
  const textoVazio = respondeu
    ? 'Nenhum boleto encontrado no período selecionado.'
    : 'Não foi possível carregar — veja o aviso acima.';

  const serie = dados?.arrecadacaoMensal ?? [];
  const situacoes = dados?.porSituacaoBoleto ?? [];
  const bases = dados?.porBase ?? [];

  return (
    <div className={carregando && respondeu ? 'do-atualizando' : undefined}>
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      {respondeu && dados.filtrosNaoAplicados.length > 0 && (
        <div className="aviso-de-tela" role="status">
          {dados.filtrosNaoAplicados.join(' e ')}{' '}
          {dados.filtrosNaoAplicados.length > 1 ? 'não se aplicam' : 'não se aplica'} a esta aba —
          os valores abaixo continuam sendo os da carteira inteira. O dinheiro é cobrado por boleto,
          e um boleto pode cobrir veículos de regionais e categorias diferentes. Para recortar por
          esses filtros, use a aba Frota &amp; Associados.
        </div>
      )}

      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 20 }}>
          Visão Geral
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      <div className="do-kpis">
        <Kpi
          rotulo="Arrecadado no período"
          valor={respondeu ? formatarMoeda(dados.kpis.valorArrecadado) : '-'}
          carregando={primeiraCarga}
          apoio="Soma dos boletos com pagamento registrado"
        />
        <Kpi
          rotulo="Emitido no período"
          valor={respondeu ? formatarMoeda(dados.kpis.valorEmitido) : '-'}
          carregando={primeiraCarga}
          apoio="Soma de todos os boletos, pagos ou não"
        />
        <Kpi
          rotulo="Adimplência"
          valor={respondeu ? formatarPercentual(dados.kpis.taxaAdimplencia) : '-'}
          carregando={primeiraCarga}
          proporcao={respondeu ? dados.kpis.taxaAdimplencia : 0}
          apoio={
            respondeu
              ? `${formatarNumero(dados.kpis.boletosPagos)} de ${formatarNumero(dados.kpis.boletosEmitidos)} boletos`
              : 'Proporção de boletos pagos'
          }
        />
        <Kpi
          rotulo="Ticket médio pago"
          valor={respondeu ? formatarMoeda(dados.kpis.ticketMedio) : '-'}
          carregando={primeiraCarga}
          apoio="Média por boleto efetivamente pago"
        />
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Emitido e arrecadado por competência"
          descricao="Cada ponto é uma competência. A distância entre as duas linhas é o que foi cobrado e não entrou naquele mês."
        >
          <div className="do-plot-alto">
            {!respondeu || vazio ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={serie} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis
                    dataKey="competencia"
                    tick={EIXO_TICK}
                    axisLine={EIXO_LINHA}
                    tickLine={false}
                  />
                  <YAxis
                    tick={EIXO_TICK}
                    axisLine={false}
                    tickLine={false}
                    width={86}
                    tickFormatter={formatarMoedaCompacta}
                  />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: any, nome: any) => [formatarMoeda(Number(valor)), nome]}
                  />
                  <Line
                    type="monotone"
                    dataKey="emitido"
                    name="Emitido"
                    stroke={COR_EMITIDO}
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 2, stroke: TINTA_GRAFICO.superficie }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="arrecadado"
                    name="Arrecadado"
                    stroke={COR_ARRECADADO}
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 2, stroke: TINTA_GRAFICO.superficie }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          {respondeu && !vazio && (
            <Legenda
              itens={[
                { rotulo: 'Emitido', cor: COR_EMITIDO },
                { rotulo: 'Arrecadado', cor: COR_ARRECADADO },
              ]}
            />
          )}
        </CardGrafico>
      </div>

      <div className="do-grid">
        <CardGrafico
          titulo="Boletos por situação"
          descricao="Quantidade de boletos em cada situação registrada na base financeira, no período."
        >
          <div className="do-plot">
            {!respondeu || situacoes.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={situacoes}
                  layout="vertical"
                  margin={{ top: 8, right: 56, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
                  <XAxis type="number" tick={EIXO_TICK} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="situacao"
                    tick={EIXO_TICK}
                    axisLine={false}
                    tickLine={false}
                    width={140}
                  />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: any) => [formatarNumero(Number(valor)), 'Boletos']}
                  />
                  {/* Série única: uma cor só para todas as barras. Colorir cada uma de um jeito
                      gastaria o canal de cor repetindo o que o comprimento já diz. */}
                  <Bar
                    dataKey="quantidade"
                    name="Boletos"
                    fill={COR_EMITIDO}
                    radius={[0, 4, 4, 0]}
                    maxBarSize={22}
                    label={{
                      position: 'right',
                      fontSize: 12,
                      fill: TINTA_GRAFICO.rotulo,
                      formatter: (v: number) => formatarNumero(v),
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>

        <CardGrafico
          titulo="Arrecadação por base"
          descricao="Quanto cada base operacional arrecadou no período. Este é o único recorte de origem aplicado a valores."
        >
          <div className="do-plot">
            {!respondeu || bases.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={bases}
                  layout="vertical"
                  margin={{ top: 8, right: 96, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
                  <XAxis
                    type="number"
                    tick={EIXO_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatarMoedaCompacta}
                  />
                  <YAxis
                    type="category"
                    dataKey="base"
                    tick={EIXO_TICK}
                    axisLine={false}
                    tickLine={false}
                    width={96}
                  />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: any) => [formatarMoeda(Number(valor)), 'Arrecadado']}
                  />
                  <Bar
                    dataKey="arrecadado"
                    name="Arrecadado"
                    fill={COR_EMITIDO}
                    radius={[0, 4, 4, 0]}
                    maxBarSize={22}
                    label={{
                      position: 'right',
                      fontSize: 12,
                      fill: TINTA_GRAFICO.rotulo,
                      formatter: (v: number) => formatarMoedaCompacta(v),
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>
      </div>

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Visão Geral"
        secoes={helpSectionsVisaoGeral}
      />
    </div>
  );
};

export default VisaoGeralTab;
